import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ParametrosEnum } from 'src/app/shared/enums/parameters.enum';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';
import { ParameterService } from '../../services/parameter.service';
import { BinanceRateComponent } from '../admin-rate/binance-rate/binance-rate.component';
import { AdminEditPermissionsService } from 'src/app/shared/services/admin-edit-permissions.service';
import { BinanceService } from '../../services/binance.service';

/** Paso a paso del cálculo, con los números vigentes, para la card explicativa. */
interface EjemploTasa {
  precioBruto: number;
  precioNeto: number;
  descuento: number;
  tasaCLP: number;
  comision: number;
  base: number;
  tasaFinal: number;
  tasaVigente: number | null;
}

@Component({
  selector: 'app-set-rates-parameters-bolivares',
  templateUrl: './set-rates-parameters-bolivares.component.html',
  styleUrls: ['./set-rates-parameters-bolivares.component.sass'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class SetRatesParametersBolivaresComponent implements OnInit {
  @ViewChild(BinanceRateComponent) binanceRateComponent!: BinanceRateComponent;

  valorSolvenciaFiat: number = 0;
  valorSolvenciaMaximaFiat: number = 0;
  valorLiquidezFiat: number = 0;
  valorDescuentoFiat: number = 0;
  setup: number = 0;
  ratesFiatForm = new FormGroup({
    setup: new FormControl(),
    valorSolvenciaFiat: new FormControl(),
    valorSolvenciaMaximaFiat: new FormControl(),
    valorLiquidezFiat: new FormControl(),
    valorDescuentoFiat: new FormControl(),
  });

  /** Candado de edición: en producción solo escribe el operador autorizado. */
  puedeEditar = false;

  /** Card explicativa: el detalle arranca plegado, la fórmula resumida siempre se ve. */
  mostrarFormula = false;

  /** Ejemplo numérico en vivo. Null mientras no haya tasa vigente cargada. */
  ejemplo: EjemploTasa | null = null;

  /** Última respuesta de `conversionRate`: de ahí salen tasaCLP y comisión. */
  private tasaVigente: any = null;

  constructor(
    private parametersService: ParameterService,
    private spinner: SpinnerService,
    private permisos: AdminEditPermissionsService,
    private binanceService: BinanceService,
  ) {
    this.parametersService.getParameter(ParametrosEnum.SETUP).then((value) => {
      this.setup = value.valor;
      this.ratesFiatForm.controls['setup'].setValue(value.valor);
    });
    this.parametersService
      .getParameter(ParametrosEnum.VALOR_SOLVENCIA_FIAT)
      .then((value) => {
        this.valorSolvenciaFiat = value.valor;
        this.ratesFiatForm.controls['valorSolvenciaFiat'].setValue(value.valor);
      });

    this.parametersService
      .getParameter(ParametrosEnum.VALOR_SOLVENCIA_MAXIMA_FIAT)
      .then((value) => {
        this.valorSolvenciaMaximaFiat = value.valor;
        this.ratesFiatForm.controls['valorSolvenciaMaximaFiat'].setValue(
          value.valor
        );
      });
    this.parametersService
      .getParameter(ParametrosEnum.VALOR_LIQUIDEZ_FIAT)
      .then((value) => {
        this.valorLiquidezFiat = value.valor;
        this.ratesFiatForm.controls['valorLiquidezFiat'].setValue(+value.valor);
      });
    this.parametersService
      .getParameter(ParametrosEnum.DESCUENTO_FIAT)
      .then((value) => {
        this.valorDescuentoFiat = value.valor;
        this.ratesFiatForm.controls['valorDescuentoFiat'].setValue(
          +value.valor
        );
        this.recalcularEjemplo();
      });
  }

  ngOnInit(): void {
    // Acá y no en el constructor: el FormGroup es un class field y no hay que
    // depender del orden de inicialización para tocarlo.
    this.aplicarPermisoEdicion(this.permisos.valorInicial);
    this.permisos.puedeEditar().then((puede) => this.aplicarPermisoEdicion(puede));
    this.cargarTasaVigente();
  }

  /**
   * Trae la tasa de cambio vigente sólo para poder mostrar el ejemplo numérico
   * de la card explicativa. Si falla, la card cae al mensaje de "cargando".
   */
  private async cargarTasaVigente(): Promise<void> {
    try {
      this.tasaVigente = await this.binanceService.getCurrentRates();
    } catch {
      this.tasaVigente = null;
    }
    this.recalcularEjemplo();
  }

  /**
   * Rehace el paso a paso del cálculo con los valores actuales.
   *
   * Ojo con `valorBanesco`: en base ya viene con el descuento restado (lo aplica
   * el cron antes de guardar), así que el precio bruto de Binance se reconstruye
   * sumándoselo de vuelta.
   */
  private recalcularEjemplo(): void {
    const tasaCLP = Number(this.tasaVigente?.tasaCLP);
    const precioNeto = Number(this.tasaVigente?.valoresVES?.valorBanesco);
    const descuento = Number(this.valorDescuentoFiat) || 0;

    if (
      !Number.isFinite(tasaCLP) ||
      tasaCLP <= 0 ||
      !Number.isFinite(precioNeto) ||
      precioNeto <= 0
    ) {
      this.ejemplo = null;
      return;
    }

    const comisionCruda = Number(this.tasaVigente?.comision);
    const comision = Number.isFinite(comisionCruda) ? comisionCruda : 0;
    const base = precioNeto / tasaCLP;
    const vigente = Number(this.tasaVigente?.maximo?.tasaBanesco);

    this.ejemplo = {
      precioBruto: precioNeto + descuento,
      precioNeto,
      descuento,
      tasaCLP,
      comision,
      base,
      tasaFinal: base * (1 - comision / 100),
      tasaVigente: Number.isFinite(vigente) && vigente > 0 ? vigente : null,
    };
  }

  /**
   * Refleja el permiso en la pantalla: sin permiso, los campos quedan
   * deshabilitados además de los botones.
   */
  private aplicarPermisoEdicion(puede: boolean): void {
    this.puedeEditar = puede;
    if (puede) {
      this.ratesFiatForm.enable({ emitEvent: false });
    } else {
      this.ratesFiatForm.disable({ emitEvent: false });
    }
  }

  reset() {
    this.ratesFiatForm.controls['valorSolvenciaFiat'].setValue(
      this.valorSolvenciaFiat
    );
    this.ratesFiatForm.controls['valorLiquidezFiat'].setValue(
      this.valorLiquidezFiat
    );
    this.ratesFiatForm.controls['valorDescuentoFiat'].setValue(
      this.valorDescuentoFiat
    );
    this.ratesFiatForm.controls['setup'].setValue(this.setup);
    this.ratesFiatForm.controls['valorSolvenciaMaximaFiat'].setValue(
      this.valorSolvenciaMaximaFiat
    );
    this.reloadData();
  }

  reloadData(): void {
    this.binanceRateComponent.initDatos();
  }

  showConfirm() {
    if (this.permisos.bloqueado(this.puedeEditar, 'Tu usuario no tiene permisos para modificar los parámetros de tasas.')) return;

    Swal.fire({
      title: '¿Está seguro de guardar los parámetros?',
      showCancelButton: true,
      confirmButtonText: `Guardar`,
      cancelButtonText: `Cancelar`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        const ref = this.spinner.start();
        await this.setParameters();
        this.spinner.stop(ref);
        Swal.fire('Guardado\n Actualizando tasas', '', 'success');
      }
    });
  }
  async setParameters() {
    const valorSolvenciaFiat =
      this.ratesFiatForm.controls['valorSolvenciaFiat'].value;
    const valorLiquidezFiat =
      this.ratesFiatForm.controls['valorLiquidezFiat'].value;
    const valorDescuentoFiat =
      this.ratesFiatForm.controls['valorDescuentoFiat'].value;
    const setup = this.ratesFiatForm.controls['setup'].value;
    const valorSolvenciaMaximaFiat =
      this.ratesFiatForm.controls['valorSolvenciaMaximaFiat'].value;

    await Promise.all([
      this.parametersService.saveParameter(
        ParametrosEnum.VALOR_SOLVENCIA_FIAT,
        valorSolvenciaFiat
      ),
      this.parametersService.saveParameter(
        ParametrosEnum.VALOR_LIQUIDEZ_FIAT,
        valorLiquidezFiat
      ),
      this.parametersService.saveParameter(
        ParametrosEnum.DESCUENTO_FIAT,
        valorDescuentoFiat
      ),
      this.parametersService.saveParameter(
        ParametrosEnum.VALOR_SOLVENCIA_MAXIMA_FIAT,
        valorSolvenciaMaximaFiat
      ),
      this.parametersService.saveParameter(ParametrosEnum.SETUP, setup),
      this.reloadData(),
    ]);
    this.valorLiquidezFiat = valorLiquidezFiat;
    this.valorSolvenciaFiat = valorSolvenciaFiat;
    this.valorDescuentoFiat = valorDescuentoFiat;
    this.setup = setup;
    this.valorSolvenciaMaximaFiat = valorSolvenciaMaximaFiat;
    this.recalcularEjemplo();
  }
}
